import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { versioned } from "@/web/webAssetsVersion";

function GoBazaarLogo({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const { mode } = useTheme();
  const fontSize = size === "sm" ? "text-lg" : size === "lg" ? "text-3xl" : "text-2xl max-[379px]:text-xl";

  if (mode === "night") {
    return (
      <span className={`flex items-center gap-1.5 select-none`}>
        <svg width="18" height="18" viewBox="0 0 14 14" fill="none" className="shrink-0">
          <path d="M7 2C5.2 2.8 4 4.6 4 7s1.2 4.2 3 5C3.4 11.4 1 9.4 1 7s2.4-4.4 6-5z" fill="#fff" opacity=".88"/>
        </svg>
        <span
          className={`gbz-wordmark gbz-logo-night ${fontSize}`}
          style={{ color: "#F0C060" }}
        >
          GOBAZAAR
        </span>
        <svg width="36" height="18" viewBox="0 0 32 17" fill="none" className="shrink-0" aria-hidden="true">
          <path className="gbz-star-1" d="M5 3l.8 2.2L8 6l-2.2.8L5 9l-.8-2.2L2 6l2.2-.8z" fill="#F4ECD8"/>
          <path className="gbz-star-2" d="M15 8l.6 1.6L17 10l-1.4.5L15 12l-.6-1.5L13 10l1.4-.4z" fill="#F4ECD8"/>
          <path className="gbz-star-3" d="M25 2l.7 1.9L27.5 4.5l-1.8.6L25 7l-.7-1.9L22.5 4.5l1.8-.6z" fill="#F4ECD8"/>
        </svg>
      </span>
    );
  }

  return (
    <span className={`gbz-wordmark ${fontSize} select-none`} style={{ color: "#C0A040" }}>
      GOBAZAAR
    </span>
  );
}

export function BrandWordmark({ size = "lg" }: { size?: "sm" | "md" | "lg" }) {
  const { mode } = useTheme();
  const fontSize =
    size === "sm" ? "1.125rem" : size === "md" ? "1.5rem" : "2.25rem";
  return (
    <span
      className="gbz-wordmark select-none"
      style={{
        fontFamily: "'Josefin Sans', 'Josefin Sans Fallback', sans-serif",
        fontWeight: 600,
        fontSize,
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        color: mode === "night" ? "#F0C060" : "#C0A040",
      }}
    >
      GOBAZAAR
    </span>
  );
}

function FooterWordmark() {
  return <BrandWordmark size="sm" />;
}

/* The Ops Center's chrome, unchanged from before the 2026-09-14 restyle except
   that the beta call to action is gone and My Profile opens the web profile.
   Only /admin renders it (see Layout below). */
function OpsLayout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { user, isAdmin, loading, signOut } = useAuth();
  const { mode, toggle } = useTheme();
  const isAdminRoute = location === "/admin" || location.startsWith("/admin/");

  // Mobile hamburger menu (FIX A) — closes on route change, Escape, or outside-click.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { setMenuOpen(false); }, [location]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMenuOpen(false); };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || menuBtnRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    // Capture phase: fires before any bubble-phase stopPropagation can swallow it.
    window.addEventListener("pointerdown", onDown, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onDown, true);
    };
  }, [menuOpen]);

  const publicNavLinks = [
    { href: "/", label: "Home" },
    { href: "/help", label: "Help" },
    { href: "/contact", label: "Contact" },
    { href: "/about", label: "About" },
  ];

  async function handleSignOut() {
    await signOut();
  }

  const navBg = mode === "day" ? "#060F1C" : "#07050F";
  const borderColor = mode === "day" ? "rgba(16,32,48,0.8)" : "rgba(28,16,53,0.8)";

  return (
    <div className="min-h-[100dvh] flex flex-col w-full" style={{ backgroundColor: "var(--gbz-bg)" }}>
      <header
        className="sticky top-0 z-50 w-full backdrop-blur supports-[backdrop-filter]:opacity-95"
        style={{ backgroundColor: navBg + "ee", borderBottom: `1px solid ${borderColor}` }}
      >
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center select-none">
            <GoBazaarLogo size="md" />
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            {publicNavLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="font-rajdhani text-sm font-semibold tracking-widest uppercase transition-colors"
                style={{
                  color: location === link.href ? "var(--gbz-gold)" : "var(--gbz-muted-fg)",
                  fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif",
                  letterSpacing: ".12em",
                }}
              >
                {link.label}
              </Link>
            ))}
            {!loading && (
              <>
                {user ? (
                  <>
                    <a
                      href="/#/profile"
                      className="font-rajdhani text-sm font-semibold tracking-widest uppercase transition-colors"
                      style={{
                        color: location === "/create-listing" ? "var(--gbz-gold)" : "var(--gbz-muted-fg)",
                        fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif",
                        letterSpacing: ".12em",
                      }}
                    >
                      My Profile
                    </a>
                    {isAdmin && (
                      <Link
                        href="/admin"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-sm border transition-colors"
                        style={{
                          fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif",
                          letterSpacing: ".12em",
                          fontSize: "11px",
                          textTransform: "uppercase",
                          borderColor: location === "/admin" ? "var(--gbz-gold)" : "rgba(192,160,64,0.4)",
                          color: "var(--gbz-gold)",
                          backgroundColor: location === "/admin" ? "rgba(192,160,64,0.12)" : "transparent",
                        }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "var(--gbz-success)" }} />
                        Ops Center
                      </Link>
                    )}
                    <Button variant="ghost" size="sm" onClick={handleSignOut} className="text-xs" style={{ color: "var(--gbz-muted-fg)", fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif", letterSpacing: ".1em" }}>
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <Link
                    href="/login"
                    className="font-rajdhani text-sm font-semibold tracking-widest uppercase transition-colors whitespace-nowrap"
                    style={{
                      color: "#F3EEE2",
                      fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif",
                      letterSpacing: ".12em",
                    }}
                  >
                    Sign In
                  </Link>
                )}
              </>
            )}
            {!isAdminRoute && (
              <button
                onClick={toggle}
                className="flex items-center justify-center px-2 py-1 rounded-sm border transition-colors"
                style={{
                  borderColor: "var(--gbz-hairline)",
                  color: "var(--gbz-muted-fg)",
                  width: 34,
                  height: 30,
                }}
                title={`Switch to ${mode === "day" ? "night" : "day"} mode`}
                aria-label={`Switch to ${mode === "day" ? "night" : "day"} mode`}
              >
                {mode === "day" ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M7 2C5.2 2.8 4 4.6 4 7s1.2 4.2 3 5C3.4 11.4 1 9.4 1 7s2.4-4.4 6-5z" fill="#8BBAD8" opacity=".9"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M7 9.5A2.5 2.5 0 107 4.5a2.5 2.5 0 000 5z" fill="#E8A820"/>
                    <g stroke="#E8A820" strokeWidth="1.1" strokeLinecap="round">
                      <path d="M7 .8v1.6M7 11.6v1.6M.8 7h1.6M11.6 7h1.6M2.6 2.6l1.1 1.1M10.3 10.3l1.1 1.1M11.4 2.6l-1.1 1.1M3.7 10.3l-1.1 1.1"/>
                    </g>
                  </svg>
                )}
              </button>
            )}
          </nav>

          {/* Mobile nav */}
          <nav className="relative flex md:hidden items-center gap-2">
            {!loading && (
              user ? (
                <>
                  <a
                    href="/#/profile"
                    className="text-xs font-semibold transition-colors"
                    style={{ color: "var(--gbz-muted-fg)", fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif", letterSpacing: ".1em" }}
                  >
                    Profile
                  </a>
                  {isAdmin && (
                    <Link
                      href="/admin"
                      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-sm border transition-colors"
                      style={{ borderColor: "rgba(192,160,64,0.4)", color: "var(--gbz-gold)", fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif", letterSpacing: ".1em" }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "var(--gbz-success)" }} />
                      Ops
                    </Link>
                  )}
                </>
              ) : (
                <Link
                  href="/login"
                  className="text-xs font-semibold transition-colors whitespace-nowrap"
                  style={{ color: "#F3EEE2", fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif", letterSpacing: ".1em" }}
                >
                  Sign In
                </Link>
              )
            )}
            {/* Mobile hamburger + dropdown menu (FIX A) — page nav links live here below md. */}
            <button
              ref={menuBtnRef}
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="flex items-center justify-center rounded-sm border"
              style={{ borderColor: "var(--gbz-hairline)", color: "var(--gbz-muted-fg)", width: 30, height: 26 }}
              aria-label="Menu"
              aria-expanded={menuOpen}
            >
              <svg width="15" height="12" viewBox="0 0 16 13" fill="none" aria-hidden="true">
                <path d="M1 1.5 H15 M1 6.5 H15 M1 11.5 H15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
            {menuOpen && (
              <div
                ref={menuRef}
                className="absolute top-full right-0 mt-2 flex flex-col rounded-lg border p-2 min-w-[190px] shadow-xl"
                style={{ backgroundColor: navBg + "f7", borderColor: "var(--gbz-hairline)", zIndex: 60 }}
              >
                {publicNavLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    className="font-rajdhani text-sm font-semibold uppercase px-3 py-2.5 rounded-sm transition-colors"
                    style={{
                      color: location === link.href ? "var(--gbz-gold)" : "var(--gbz-muted-fg)",
                      fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif",
                      letterSpacing: ".14em",
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
                {!loading && user && (
                  <>
                    <a
                      href="/#/profile"
                      onClick={() => setMenuOpen(false)}
                      className="font-rajdhani text-sm font-semibold uppercase px-3 py-2.5 rounded-sm transition-colors"
                      style={{
                        color: location === "/create-listing" ? "var(--gbz-gold)" : "var(--gbz-muted-fg)",
                        fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif",
                        letterSpacing: ".14em",
                      }}
                    >
                      My Profile
                    </a>
                    {isAdmin && (
                      <Link
                        href="/admin"
                        onClick={() => setMenuOpen(false)}
                        className="font-rajdhani text-sm font-semibold uppercase px-3 py-2.5 rounded-sm transition-colors"
                        style={{
                          color: location === "/admin" ? "var(--gbz-gold)" : "var(--gbz-muted-fg)",
                          fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif",
                          letterSpacing: ".14em",
                        }}
                      >
                        Ops Center
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); handleSignOut(); }}
                      className="text-left font-rajdhani text-sm font-semibold uppercase px-3 py-2.5 rounded-sm transition-colors"
                      style={{
                        color: "var(--gbz-muted-fg)",
                        fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif",
                        letterSpacing: ".14em",
                      }}
                    >
                      Sign Out
                    </button>
                  </>
                )}
                {!isAdminRoute && (
                  <button
                    type="button"
                    onClick={toggle}
                    className="flex items-center gap-2 text-left font-rajdhani text-sm font-semibold uppercase px-3 py-2.5 rounded-sm transition-colors"
                    style={{
                      color: "var(--gbz-muted-fg)",
                      fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif",
                      letterSpacing: ".14em",
                      borderTop: "1px solid var(--gbz-hairline)",
                      marginTop: "2px",
                      paddingTop: "10px",
                    }}
                    aria-label={`Switch to ${mode === "day" ? "night" : "day"} mode`}
                  >
                    {mode === "day" ? (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M7 2C5.2 2.8 4 4.6 4 7s1.2 4.2 3 5C3.4 11.4 1 9.4 1 7s2.4-4.4 6-5z" fill="#8BBAD8" opacity=".9"/>
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M7 9.5A2.5 2.5 0 107 4.5a2.5 2.5 0 000 5z" fill="#E8A820"/>
                        <g stroke="#E8A820" strokeWidth="1.1" strokeLinecap="round">
                          <path d="M7 .8v1.6M7 11.6v1.6M.8 7h1.6M11.6 7h1.6M2.6 2.6l1.1 1.1M10.3 10.3l1.1 1.1M11.4 2.6l-1.1 1.1M3.7 10.3l-1.1 1.1"/>
                        </g>
                      </svg>
                    )}
                    {mode === "day" ? "Night Mode" : "Day Mode"}
                  </button>
                )}
              </div>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1">
        {children}
      </main>

      <footer
        className="py-12 mt-auto"
        style={{ borderTop: "1px solid var(--gbz-hairline)", backgroundColor: "var(--gbz-card)" }}
      >
        <div className="container mx-auto px-4 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="space-y-4">
            <Link href="/" className="flex items-center select-none">
              <FooterWordmark />
            </Link>
          </div>
          <div>
            <h4
              className="font-semibold mb-4 text-xs uppercase tracking-wider"
              style={{ color: "var(--gbz-muted-fg)", fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif", letterSpacing: ".16em" }}
            >
              Resources
            </h4>
            <ul className="space-y-2 text-sm" style={{ color: "var(--gbz-muted-fg)" }}>
              <li><Link href="/help" className="hover:opacity-80 transition-opacity" style={{ fontFamily: "'Space Grotesk', 'Space Grotesk Fallback', sans-serif" }}>Help Center</Link></li>
              <li><Link href="/contact" className="hover:opacity-80 transition-opacity" style={{ fontFamily: "'Space Grotesk', 'Space Grotesk Fallback', sans-serif" }}>Submit Ticket</Link></li>
              <li><Link href="/about" className="hover:opacity-80 transition-opacity" style={{ fontFamily: "'Space Grotesk', 'Space Grotesk Fallback', sans-serif" }}>About Us</Link></li>
            </ul>
          </div>
          <div>
            <h4
              className="font-semibold mb-4 text-xs uppercase tracking-wider"
              style={{ color: "var(--gbz-muted-fg)", fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif", letterSpacing: ".16em" }}
            >
              Legal
            </h4>
            <ul className="space-y-2 text-sm" style={{ color: "var(--gbz-muted-fg)" }}>
              <li><Link href="/terms" className="hover:opacity-80 transition-opacity" style={{ fontFamily: "'Space Grotesk', 'Space Grotesk Fallback', sans-serif" }}>Terms of Service</Link></li>
              <li><Link href="/privacy" className="hover:opacity-80 transition-opacity" style={{ fontFamily: "'Space Grotesk', 'Space Grotesk Fallback', sans-serif" }}>Privacy Policy</Link></li>
              <li><Link href="/refund-policy" className="hover:opacity-80 transition-opacity" style={{ fontFamily: "'Space Grotesk', 'Space Grotesk Fallback', sans-serif" }}>Refund Policy</Link></li>
              <li><Link href="/dmca" className="hover:opacity-80 transition-opacity" style={{ fontFamily: "'Space Grotesk', 'Space Grotesk Fallback', sans-serif" }}>DMCA / Copyright</Link></li>
              <li><Link href="/accessibility" className="hover:opacity-80 transition-opacity" style={{ fontFamily: "'Space Grotesk', 'Space Grotesk Fallback', sans-serif" }}>Accessibility</Link></li>
              <li><Link href="/help/escrow" className="hover:opacity-80 transition-opacity" style={{ fontFamily: "'Space Grotesk', 'Space Grotesk Fallback', sans-serif" }}>Escrow Rules</Link></li>
              <li><Link href="/privacy#your-privacy-rights" className="hover:opacity-80 transition-opacity" style={{ fontFamily: "'Space Grotesk', 'Space Grotesk Fallback', sans-serif" }}>Do Not Sell or Share My Personal Information</Link></li>
            </ul>
          </div>
          <div>
            <h4
              className="font-semibold mb-4 text-xs uppercase tracking-wider"
              style={{ color: "var(--gbz-muted-fg)", fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif", letterSpacing: ".16em" }}
            >
              System
            </h4>
            <ul className="space-y-2 text-sm" style={{ color: "var(--gbz-muted-fg)" }}>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "var(--gbz-success)" }} />
                <span style={{ fontFamily: "'Space Grotesk', 'Space Grotesk Fallback', sans-serif" }}>All systems operational</span>
              </li>
              <li><a href="mailto:security@gobazaar.co" className="hover:opacity-80 transition-opacity" style={{ fontFamily: "'Space Grotesk', 'Space Grotesk Fallback', sans-serif" }}>security@gobazaar.co</a></li>
            </ul>
          </div>
        </div>
        <div
          className="container mx-auto px-4 mt-10 pt-6 flex items-center justify-between text-xs"
          style={{ borderTop: "1px solid rgba(255,255,255,0.05)", color: "var(--gbz-muted-fg)", fontFamily: "'Rajdhani', 'Rajdhani Fallback', sans-serif", letterSpacing: ".1em" }}
        >
          <span>© 2026 GOBAZAAR. All rights reserved.</span>
          <span className="gbz-wordmark" style={{ fontSize: "14px", color: "var(--gbz-wordmark)", opacity: 0.5 }}>GOBAZAAR</span>
        </div>
      </footer>
    </div>
  );
}

/* ── The web's own chrome, for every page outside the web app ──────────────
   Blake, 2026-09-14: About, Help and its guides, Contact, the legal pages, and
   the account pages had their own dark header and footer. They now wear the
   web app's header and footer, built from the web's own stylesheet
   (public/web/styles.scoped.css plus overrides.css) and class names, so the
   two stay identical when the web changes. That stylesheet is wrapped in
   @scope (.blake-install-scope), so it only reaches inside the two wrappers
   below and never the React page between them.

   Links mirror renderNav and renderFooter in public/web/app.js, with the real
   destinations src/web/linkFixups.ts writes onto the web footer. The web's
   hash routes are reached with plain anchors to "/#/…": the web app is mounted
   at "/", outside this router. */

const WEB_CHROME_STYLESHEETS = [versioned("/web/styles.scoped.css"), versioned("/web/overrides.css")];

const INBOX_ICON = (
  <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 4 h14 v10 h-14 Z" />
    <path d="M2 10 h4 l1.5 2 h3 l1.5 -2 h4" />
  </svg>
);

/* Loads the web's stylesheets once. Either side reuses a sheet the other already
   loaded: this hook reuses WebInstallHost's tags, and WebInstallHost reuses these. Resolves true when both have loaded, or after a
   short timeout so a failed load shows unstyled chrome instead of none. */
export function useWebChromeStylesheets(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const waits = WEB_CHROME_STYLESHEETS.map((href) => {
      let link = document.querySelector<HTMLLinkElement>(`link[rel="stylesheet"][href="${href}"]`);
      if (link && link.sheet) return Promise.resolve();
      if (!link) {
        link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.setAttribute("data-web-chrome", "1");
        document.head.appendChild(link);
      }
      const el = link;
      return new Promise<void>((resolve) => {
        el.addEventListener("load", () => resolve(), { once: true });
        el.addEventListener("error", () => resolve(), { once: true });
      });
    });
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 3000));
    Promise.race([Promise.all(waits).then(() => undefined), timeout]).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => { cancelled = true; };
  }, []);
  return ready;
}

function initialsFor(username: string): string {
  return username.replace(/[^a-z0-9]/gi, "").slice(0, 2).toUpperCase() || "?";
}

/* Anchors carry the web's button classes, which were written for <button>.
   A button centers its content on its own; an anchor needs this. */
const ANCHOR_BUTTON: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center" };

function WebChromeLayout({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, loading } = useAuth();
  const { mode } = useTheme();
  const stylesReady = useWebChromeStylesheets();
  const logo = `/web/assets/logo-${mode === "night" ? "night" : "day"}.svg`;
  /* The scoped sheet gives its root min-height: 100dvh (it was written for the
     whole page). These wrappers hold only the header or the footer. */
  const scopeStyle: React.CSSProperties = { minHeight: 0, visibility: stylesReady ? "visible" : "hidden" };

  return (
    <div className="min-h-[100dvh] flex flex-col w-full" style={{ backgroundColor: "var(--gbz-bg)" }}>
      <div
        className="blake-install-scope"
        data-mode={mode}
        data-web-chrome="header"
        style={{ ...scopeStyle, position: "sticky", top: 0, zIndex: 40 }}
      >
        <div className="nav">
          <div className="nav-inner">
            <a className="brand" href="/" aria-label="GOBAZAAR home">
              <img className="logo-img" src={logo} alt="GOBAZAAR" />
            </a>
            <div className="nav-actions">
              <a className="btn-list" href="/#/list" style={ANCHOR_BUTTON}>+ List an item</a>
              {!loading && (user ? (
                <>
                  <a className="btn-inbox" href="/#/deals">
                    {INBOX_ICON}
                    <span className="only-desktop">Deals</span>
                  </a>
                  {isAdmin && <a className="btn-inbox" href="/admin">Admin</a>}
                  <a className="avatar-chip" href="/#/profile" aria-label="Your profile">
                    <span className="avatar avatar-sm">{initialsFor(user.username)}</span>
                    <span className="only-desktop" style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>
                      {user.username}
                    </span>
                  </a>
                </>
              ) : (
                <a className="btn-signin" href="/#/auth/signin" style={ANCHOR_BUTTON}>Sign in</a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1">{children}</main>

      <div className="blake-install-scope" data-mode={mode} data-web-chrome="footer" style={scopeStyle}>
        <footer>
          <div className="wrap">
            <div className="foot-grid">
              <div>
                <a className="brand" href="/" aria-label="GOBAZAAR home">
                  <img className="logo-img" src={logo} alt="GOBAZAAR" />
                </a>
                <p className="foot-tag">Buy. Sell. Barter. Escrow-protected trading, day and night.</p>
              </div>
              <div className="foot-col">
                <div className="h microlabel">Market</div>
                <a href="/#/discover">Discover</a>
                <a href="/#/">Browse listings</a>
                <a href="/#/collections">Top collections</a>
                <a href="/#/deals">Your deals</a>
                <a href="/#/profile">Your collection</a>
              </div>
              <div className="foot-col">
                <div className="h microlabel">Trust</div>
                <a href="/help">How it works</a>
                <a href="/help/escrow">Escrow on every deal</a>
                <span style={{ display: "block", color: "var(--ink-muted)", padding: "5px 0", fontSize: 14.5 }}>Price checks</span>
                <a href="/help/reputation">Community verdicts</a>
              </div>
              <div className="foot-col">
                <div className="h microlabel">Company</div>
                <a href="/about">About</a>
                <a href="/terms">Terms</a>
                <a href="/privacy">Privacy</a>
                <a href="/help">Support</a>
                <a href="/dmca">DMCA</a>
                <a href="/refund-policy">Refund Policy</a>
                <a href="/contact">Contact</a>
                <a href="/accessibility">Accessibility</a>
                <a href="/privacy#your-privacy-rights">Do Not Sell or Share My Personal Information</a>
              </div>
            </div>
            <div className="foot-note">
              <span>gobazaar.co</span>
              <span>© {new Date().getFullYear()} GOBAZAAR</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const isAdminRoute = location === "/admin" || location.startsWith("/admin/");
  if (isAdminRoute) return <OpsLayout>{children}</OpsLayout>;
  return <WebChromeLayout>{children}</WebChromeLayout>;
}
